<script lang="ts">
	/**
	 * A Flowbite `Button` that navigates through the router.
	 *
	 * Passing `href` to `Button` makes it render a real `<a>` — which is what we
	 * want for accessibility and middle-click/"open in new tab" — but a plain
	 * anchor click would reload the whole SPA. So the click is intercepted and
	 * turned into a `push()`.
	 *
	 * ```svelte
	 * <RoutedButton href="/Settings/Admin/Customers" color="primary" size="sm">
	 *   <i class="fa-solid fa-users mr-2" />View users
	 * </RoutedButton>
	 * ```
	 */

	import { Button } from 'flowbite-svelte';
	import { push } from '../svelte-spa-router';

	/** Destination path. Omit to get a plain button that only fires `on:click`. */
	export let href: string | undefined = undefined;
	/** Anchor target. Any value other than the default self-target opts out of routing. */
	export let target: string | undefined = undefined;

	export let pill: boolean | undefined = undefined;
	export let outline: boolean | undefined = undefined;
	export let size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | undefined = undefined;
	export let type: 'button' | 'submit' | 'reset' | undefined = undefined;
	export let color: 'red' | 'yellow' | 'green' | 'purple' | 'blue' | 'light' | 'dark' | 'primary' | 'none' | 'alternative' | undefined = undefined;
	export let shadow: boolean | undefined = undefined;
	/** Blocks navigation and dims the button. */
	export let disabled: boolean | undefined = undefined;

	let cssClass: string | undefined = undefined;
	export { cssClass as class };

	/**
	 * Runs before navigating; return `false` to cancel it. This is the hook for
	 * "are you sure?" confirmations and unsaved-changes guards — the button stays
	 * a link, but the navigation is conditional.
	 */
	export let clickHandler: () => boolean = () => true;

	// The router only understands in-app paths. External URLs, new-tab targets and
	// hrefless buttons must keep the browser's native behaviour, and modifier-clicks
	// (ctrl/cmd/shift/middle) must stay "open in a new tab" or the link feels broken.
	const fnNavigate = (event: MouseEvent) => {
		if (!href || !href.startsWith('/') || href.startsWith('//')) return;
		if (target && target !== '_self') return;
		if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;

		event.preventDefault();

		if (disabled) return;
		if (!clickHandler()) return;

		push(href);
	};
</script>

<Button class={cssClass} {href} {target} {size} {color} {type} {shadow} {pill} {outline} {disabled} on:click={fnNavigate} on:click>
	<slot />
</Button>
